import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  NutrientClashEyebrow,
  NutrientClashHeading,
  NutrientClashHeadingEm,
  NutrientClashLede,
  NutrientClashLeftLabel,
  NutrientClashLeftCaption,
  NutrientClashRightLabel,
  NutrientClashRightCaption,
  NutrientClashAtOnce,
  NutrientClashOverTime,
  NutrientClashProof1,
  NutrientClashProof2,
  NutrientClashProof3,
} from '../../../Constants';
import { Icon } from '../../elements/icon';

// Illustrative nutrient colors — deliberately off-palette so multiple dots don't
// clash with navy/teal/butter. Shared across both diagrams to read as
// "same nutrients, different delivery".
const NUTRIENT_COLORS = ['#C98A3A', '#7A9B3A', '#3A7A9B', '#8A5FA3', '#B5472F'];

const ProofPill = ({ children }) => (
  <div className="t-flex t-items-center t-gap-2.5 t-rounded-full t-border t-border-[#C7EADF] t-bg-[#EAF7F4] t-px-3.5 t-py-2 t-font-sans t-text-[14px] t-leading-tight t-text-navy">
    <span className="t-flex t-h-[18px] t-w-[18px] t-flex-none t-items-center t-justify-center t-rounded-full t-bg-[#168766]">
      <Icon raw name="quizEmailCheck" size={11} color="#FFFFFF" />
    </span>
    <span>{children}</span>
  </div>
);

const NutrientClashSection = () => {
  const { t } = useTranslation();

  return (
    <div className="t-w-full t-max-w-[640px] t-mx-auto t-px-4 t-flex t-flex-col">
      <p className="!t-m-0 t-text-center t-font-sans t-text-xs t-font-bold t-uppercase t-tracking-[0.15em] t-text-[#116D6E]">
        {t(NutrientClashEyebrow)}
      </p>
      <h2 className="!t-mt-2.5 !t-mb-0 t-text-center t-font-serif t-font-normal t-leading-[1.04] t-text-navy">
        {t(NutrientClashHeading)}{' '}
        <em className="t-italic t-text-[#116D6E]">{t(NutrientClashHeadingEm)}</em>
      </h2>
      <p className="!t-mt-3 !t-mb-0 t-mx-auto t-max-w-[46ch] t-text-center t-font-sans t-text-[15px] t-leading-[1.55] t-text-navy/70">
        {t(NutrientClashLede)}
      </p>

      <div className="t-mt-7 t-grid t-grid-cols-1 sm:t-grid-cols-2 t-gap-3.5">
        {/* LEFT — pressed pills & powders */}
        <div className="t-rounded-xl t-border t-border-g200 t-bg-white t-p-4 t-pb-3">
          <p className="!t-m-0 t-flex t-items-center t-gap-2 t-font-sans t-text-[13px] t-font-bold t-text-navy">
            <span
              className="t-h-2 t-w-2 t-flex-none t-rounded-full"
              style={{ backgroundColor: '#C0A05A' }}
            />
            {t(NutrientClashLeftLabel)}
          </p>
          <svg
            className="t-mt-3 t-w-full t-h-auto"
            viewBox="0 0 200 130"
            xmlns="http://www.w3.org/2000/svg"
            role="img"
            aria-label={t(NutrientClashLeftCaption)}
          >
            {/* tablet */}
            <ellipse cx="60" cy="58" rx="40" ry="31" fill="#EFE7D3" stroke="#D8C98F" strokeWidth="2" />
            <line x1="60" y1="30" x2="60" y2="86" stroke="#D8C98F" strokeWidth="1.5" opacity="0.55" />
            <circle cx="50" cy="52" r="8" fill={NUTRIENT_COLORS[0]} />
            <circle cx="64" cy="49" r="8" fill={NUTRIENT_COLORS[1]} />
            <circle cx="56" cy="64" r="8" fill={NUTRIENT_COLORS[2]} />
            <circle cx="70" cy="63" r="8" fill={NUTRIENT_COLORS[3]} />
            <g stroke="#8A3B2A" strokeWidth="2" strokeLinecap="round">
              <path d="M58 55 l5 5 M63 55 l-5 5" />
            </g>
            {/* powder heap */}
            <path d="M108 92 Q140 40 172 92 Z" fill="#EFE7D3" stroke="#D8C98F" strokeWidth="2" />
            <circle cx="132" cy="78" r="5.5" fill={NUTRIENT_COLORS[4]} />
            <circle cx="145" cy="72" r="5.5" fill={NUTRIENT_COLORS[1]} />
            <circle cx="140" cy="84" r="5.5" fill={NUTRIENT_COLORS[2]} />
            <circle cx="152" cy="82" r="5.5" fill={NUTRIENT_COLORS[0]} />
            <circle cx="128" cy="86" r="5.5" fill={NUTRIENT_COLORS[3]} />
            <g stroke="#8A3B2A" strokeWidth="1.8" strokeLinecap="round">
              <path d="M140 76 l4 4 M144 76 l-4 4" />
            </g>
            {/* baseline */}
            <line x1="14" y1="92" x2="186" y2="92" stroke="#E4DAC1" strokeWidth="2" />
            <text
              x="100"
              y="110"
              textAnchor="middle"
              fontFamily="'Plus Jakarta Sans', sans-serif"
              fontSize="9"
              fill="#A08A55"
              letterSpacing="0.04em"
            >
              {t(NutrientClashAtOnce)}
            </text>
          </svg>
          <p className="!t-mt-3 !t-mb-0 t-font-sans t-text-[12.5px] t-leading-[1.45] t-text-navy/60">
            {t(NutrientClashLeftCaption)}
          </p>
        </div>

        {/* RIGHT — riise microgranule */}
        <div className="t-rounded-xl t-border t-border-[#C7EADF] t-bg-[#F6FBF9] t-p-4 t-pb-3">
          <p className="!t-m-0 t-flex t-items-center t-gap-2 t-font-sans t-text-[13px] t-font-bold t-text-navy">
            <span
              className="t-h-2 t-w-2 t-flex-none t-rounded-full"
              style={{ backgroundColor: '#116D6E' }}
            />
            {t(NutrientClashRightLabel)}
          </p>
          <svg
            className="t-mt-3 t-w-full t-h-auto"
            viewBox="0 0 200 130"
            xmlns="http://www.w3.org/2000/svg"
            role="img"
            aria-label={t(NutrientClashRightCaption)}
          >
            {/* time baseline with arrow */}
            <line x1="14" y1="92" x2="186" y2="92" stroke="#C7EADF" strokeWidth="2" />
            <path
              d="M182 88 l6 4 l-6 4"
              fill="none"
              stroke="#C7EADF"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            {/* granules: colored core + dashed protective shell, staggered along time */}
            <g>
              <circle cx="34" cy="70" r="13" fill="none" stroke="#C7EADF" strokeWidth="2" strokeDasharray="3 3" />
              <circle cx="34" cy="70" r="8" fill={NUTRIENT_COLORS[0]} />
              <line x1="34" y1="83" x2="34" y2="92" stroke="#C7EADF" strokeWidth="1.5" />
            </g>
            <g>
              <circle cx="78" cy="48" r="13" fill="none" stroke="#C7EADF" strokeWidth="2" strokeDasharray="3 3" />
              <circle cx="78" cy="48" r="8" fill={NUTRIENT_COLORS[1]} />
              <line x1="78" y1="61" x2="78" y2="92" stroke="#C7EADF" strokeWidth="1.5" />
            </g>
            <g>
              <circle cx="122" cy="62" r="13" fill="none" stroke="#C7EADF" strokeWidth="2" strokeDasharray="3 3" />
              <circle cx="122" cy="62" r="8" fill={NUTRIENT_COLORS[2]} />
              <line x1="122" y1="75" x2="122" y2="92" stroke="#C7EADF" strokeWidth="1.5" />
            </g>
            <g>
              <circle cx="166" cy="42" r="13" fill="none" stroke="#C7EADF" strokeWidth="2" strokeDasharray="3 3" />
              <circle cx="166" cy="42" r="8" fill={NUTRIENT_COLORS[3]} />
              <line x1="166" y1="55" x2="166" y2="92" stroke="#C7EADF" strokeWidth="1.5" />
            </g>
            <text
              x="100"
              y="110"
              textAnchor="middle"
              fontFamily="'Plus Jakarta Sans', sans-serif"
              fontSize="9"
              fill="#5C8F86"
              letterSpacing="0.05em"
            >
              {t(NutrientClashOverTime)}
            </text>
          </svg>
          <p className="!t-mt-3 !t-mb-0 t-font-sans t-text-[12.5px] t-leading-[1.45] t-text-navy/60">
            {t(NutrientClashRightCaption)}
          </p>
        </div>
      </div>

      <div className="t-mt-6 t-mx-auto t-flex t-w-full t-max-w-[440px] t-flex-col t-gap-2.5">
        <ProofPill>{t(NutrientClashProof1)}</ProofPill>
        <ProofPill>{t(NutrientClashProof2)}</ProofPill>
        <ProofPill>{t(NutrientClashProof3)}</ProofPill>
      </div>
    </div>
  );
};

export default NutrientClashSection;
